import { S } from './state.js';
import { Audio } from './audio.js';
import { BAL } from './config.js';
import { ARCH_INFO, ITEMS, SYNERGY_INFO, TIERS } from './data/items.js';
import { ACHIEVEMENTS, UNLOCKS, UPGRADES, accountLevel } from './data/progression.js';
import { ITEM_PREREQ, MODE_RULES, WEAPONS, WEAPON_IDX, evoEffectStr, prereqMet } from './data/weapons.js';
import { syncNameField } from './main.js';
import { COMPANIONS, COMPANION_IDX, persist, totalItems } from './economy.js';
import { drawCompanion, drawMines, drawMirror } from './allies.js';
import { cam, srcMeta } from './meter.js';
import { enemyColor, player } from './entities.js';
import { drawArena, drawAuras, drawGroundFires, drawHazards, drawMortars, drawRocks, drawWorldEdge } from './world.js';
import { ctx } from './canvas.js';
import { aim, pointIn, startRun } from './input.js';
import { AFFIX_INFO } from './combat.js';
import { abbr, bg, drawCoins, drawEffects, drawParticles, drawPickups, drawPlayer, drawShrines, fmtTime, glowSprite, hexA } from './render.js';
import { buyUpgrade, upgradeCost } from './stats.js';
import { hud } from './touch-hud.js';
import { drawTooltips, fitText, fitTip, wrapText } from './ui.js';
// ---- Item Journal (press J): a scrollable codex of every item, grouped by tier ----
export function journalMenu() {
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.93)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(760, S.W - 24), ph = Math.min(640, S.H - 24);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(12,18,38,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = 'bold 26px system-ui';
  ctx.fillText('📖 JOURNAL', px + 28, py + 42);
  // tabs — across the top on wide panels, but their OWN full-width row on narrow (phone) screens, where
  // the 5 fixed-width tabs otherwise overflow off the right edge and SYNERGY/DAMAGE/ACH are unreachable
  const tabData = [['items', 'ITEMS', 'ITEMS'], ['weapons', '🔫 WEAPONS', 'WEAPONS'], ['synergy', '⚛ SYNERGY', 'SYNERGY'], ['damage', '⚔ DAMAGE', 'DAMAGE'], ['achievements', '🏅 ACH.', 'ACH']];
  const tabsOwnRow = pw < 620;
  const tOff = tabsOwnRow ? 36 : 0;   // everything below the tabs shifts down by this on narrow
  if (tabsOwnRow) {
    const tGap = 6, tw = (pw - 36 - tGap * 4) / 5, tyy = py + 56, th = 30;
    let _tx = px + 18;
    for (const [tkey, , short] of tabData) {
      const active = S.journalTab === tkey;
      ctx.fillStyle = active ? '#2b6fd6' : 'rgba(255,255,255,.06)'; roundRect(_tx, tyy, tw, th, 8); ctx.fill();
      ctx.fillStyle = active ? '#fff' : '#9fb0e0'; ctx.font = 'bold 10px system-ui';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(fitText(short, tw - 6), _tx + tw / 2, tyy + th / 2 + 1);
      const k = tkey; S.uiButtons.push({ x: _tx, y: tyy, w: tw, h: th, action: () => { S.journalTab = k; S.journalScroll = 0; } });
      _tx += tw + tGap;
    }
  } else {
    let _tx = px + 196;
    for (const [tkey, label] of tabData) {
      const active = S.journalTab === tkey, tw = tkey === 'items' ? 68 : tkey === 'weapons' ? 106 : tkey === 'synergy' ? 100 : tkey === 'damage' ? 96 : 76, tyy = py + 22, th = 30;
      ctx.fillStyle = active ? '#2b6fd6' : 'rgba(255,255,255,.06)'; roundRect(_tx, tyy, tw, th, 8); ctx.fill();
      ctx.fillStyle = active ? '#fff' : '#9fb0e0'; ctx.font = 'bold 12px system-ui';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(label, _tx + tw / 2, tyy + th / 2 + 1);
      const k = tkey; S.uiButtons.push({ x: _tx, y: tyy, w: tw, h: th, action: () => { S.journalTab = k; S.journalScroll = 0; } });
      _tx += tw + 8;
    }
  }
  { // ✕ close button — the journal was J/Esc-only, which a phone doesn't have (0.18, touch pass)
    const cs = 30, cxx = px + pw - cs - 16, cyy = py + 22;
    ctx.fillStyle = 'rgba(255,255,255,.08)'; roundRect(cxx, cyy, cs, cs, 8); ctx.fill();
    ctx.fillStyle = '#9fb0e0'; ctx.font = 'bold 15px system-ui';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('✕', cxx + cs / 2, cyy + cs / 2 + 1);
    S.uiButtons.push({ x: cxx, y: cyy, w: cs, h: cs, action: () => { S.showJournal = false; } });
  }
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  const _achN = ACHIEVEMENTS.filter(a => (S.save.achievements || {})[a.key]).length;
  ctx.fillStyle = '#6b76a0'; ctx.font = '13px system-ui';
  const _distinct = Object.keys(S.items).filter(k => S.items[k]).length;
  ctx.fillText(S.journalTab === 'items'
    ? (ITEMS.length + ' items' + (_distinct ? ' · YOU HOLD ' + totalItems() + ' (' + _distinct + ' distinct — shown first, ×count)' : '') + ' · J or Esc to close')
    : S.journalTab === 'weapons'
    ? 'every weapon + its evolution (reach ×' + BAL.evo.need + ' in its archetype) · J or Esc to close'
    : S.journalTab === 'synergy'
    ? 'how systems combine — reactions, marks & the coin web · J or Esc to close'
    : S.journalTab === 'damage'
    ? 'damage dealt this run, by source · J or Esc to close'
    : (_achN + ' / ' + ACHIEVEMENTS.length + ' unlocked · scroll to browse'), px + 28, py + 64 + tOff);

  // clipped scroll viewport (shifted down past the tab row on narrow screens)
  const vpX = px + 18, vpY = py + 82 + tOff, vpW = pw - 36, vpH = ph - 82 - tOff - 72;
  ctx.save();
  roundRect(vpX, vpY, vpW, vpH, 12); ctx.clip();

  let cy0 = 8;   // running content height (relative to viewport top)
  if (S.journalTab === 'items') {
  // mechanics legend (player questions): how DoTs behave on re-hits + where the hard caps sit
  const legend = [
    'ℹ poison STACKS per hit (cap 10 + poison items); each stack ticks base + 7% your DMG + 0.38% target max HP per second · burn RAMPS to ×2.5 on re-hits · chill refreshes & counts toward freeze',
    'ℹ caps: armor 60% (deep tank 72%) · dodge 40% · crit 95% · crit dmg soft-caps past 4× · burn 85% · poison 92% · slow 75% · combat leech 15% HP/s · fire rate KNEES at 12 frames/shot (further stacking counts half, 0.19) · OVERFLOW past a cap leaks sideways: burn/poison can apply twice · crit past 95% can crit ×2 · slow past 75% freezes faster · capped leech speeds shield recharge · fire rate past the floor becomes damage (×2 max) · capped/unusable items stop appearing in shops; the stats panel (B) paints capped stats red',
    'ℹ reactions: ⚡OVERLOAD — lightning striking a BURNING foe consumes the burn & detonates · 💥DETONATE — a blast hitting a burning/poisoned foe consumes the DoT for a burst',
    'ℹ rarity odds — caches & drops: 60% common · 25% uncommon · 9% epic · 4% legendary · 2% lunar. Blood pacts & boss spoils roll RICHER on purpose (boss gear 65% legendary, blood biased epic+) — that is why legendaries can feel common',
  ];
  for (const ln of legend) {
    const ly2 = vpY - S.journalScroll + cy0;
    if (ly2 + 14 > vpY && ly2 < vpY + vpH) {
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#8a97c4'; ctx.font = '11px system-ui';
      ctx.fillText(fitTip(ln, vpW - 24, vpX + 8, ly2, 15), vpX + 8, ly2 + 7);
    }
    cy0 += 17;
  }
  cy0 += 8;
  // 1 column on narrow (phone) so names + descriptions are readable instead of truncating to "Lodes…"
  const cols = vpW < 380 ? 1 : 2;
  const colGap = 10, colW = (vpW - 16 - colGap * (cols - 1)) / cols, rowH = 54, rowGap = 8;
  for (const tier of ['common', 'uncommon', 'epic', 'legendary', 'lunar', 'mythic']) {
    // owned items float to the top of their tier — the journal doubles as the run's full inventory
    // (the pause menu folds long item lists into "+N more — see Journal")
    const list = ITEMS.filter(i => i.tier === tier)
      .sort((a, b) => ((S.items[b.key] || 0) > 0 ? 1 : 0) - ((S.items[a.key] || 0) > 0 ? 1 : 0));
    if (!list.length) continue;
    const col = TIERS[tier].color;
    const hy = vpY - S.journalScroll + cy0;            // section header
    if (hy + 22 > vpY && hy < vpY + vpH) {
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillStyle = hexA(col, 0.95); ctx.font = 'bold 14px system-ui';
      ctx.fillText('● ' + tier.toUpperCase() + '  ·  ' + list.length, vpX + 8, hy + 12);
    }
    cy0 += 28;
    list.forEach((it, i) => {
      const cx = vpX + 8 + (i % cols) * (colW + colGap);
      const cy = vpY - S.journalScroll + cy0 + Math.floor(i / cols) * (rowH + rowGap);
      if (cy + rowH < vpY || cy > vpY + vpH) return;   // cull rows scrolled out of view
      const n = S.items[it.key] || 0;                    // owned this run → highlighted card + ×count badge
      const pq = ITEM_PREREQ[it.key];                    // unmet prereq & unowned → "locked": amber, hidden from offers
      const locked = pq && !n && !prereqMet(it.key);
      ctx.fillStyle = n ? hexA(col, 0.10) : 'rgba(255,255,255,.04)'; roundRect(cx, cy, colW, rowH, 9); ctx.fill();
      ctx.strokeStyle = locked ? 'rgba(255,183,77,.5)' : hexA(col, n ? 0.9 : 0.45); ctx.lineWidth = n ? 2 : 1.3; ctx.stroke();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = '24px system-ui'; ctx.fillStyle = '#fff';
      ctx.fillText(it.icon, cx + 26, cy + rowH / 2);
      ctx.textAlign = 'left';
      ctx.font = 'bold 14px system-ui'; ctx.fillStyle = hexA(col, 1);
      ctx.fillText(fitTip(it.name, colW - 92, cx + 48, cy + 13, 16), cx + 48, cy + 21);
      ctx.font = '12px system-ui'; ctx.fillStyle = '#a9b5df';
      ctx.fillText(fitTip(it.desc, colW - 60, cx + 48, cy + 31, 16), cx + 48, cy + 39);
      if (n) {
        ctx.textAlign = 'right'; ctx.font = 'bold 14px system-ui'; ctx.fillStyle = hexA(col, 1);
        ctx.fillText('×' + n, cx + colW - 12, cy + 21);
        ctx.textAlign = 'left';
      } else if (locked) {   // teach the dependency the offer pool now hides: "🔒 needs 🔥 ignite"
        ctx.textAlign = 'right'; ctx.font = '10px system-ui'; ctx.fillStyle = '#ffb74d';
        ctx.fillText(fitText('🔒 needs ' + pq.short, colW - 56), cx + colW - 12, cy + 14);
        ctx.textAlign = 'left';
      }
    });
    cy0 += Math.ceil(list.length / cols) * (rowH + rowGap) + 14;
  }
  } else if (S.journalTab === 'weapons') {   // weapon codex: base form + evolution + ascension + item-interaction rules
    const rowH = 98, rowGap = 8;
    WEAPONS.forEach((w, i) => {
      const cy = vpY - S.journalScroll + cy0 + i * (rowH + rowGap);
      if (cy + rowH < vpY || cy > vpY + vpH) return;   // cull rows scrolled out of view
      const held = i === S.weapon && S.started && !S.dead;
      const evolved = held && S.EVO;
      ctx.fillStyle = held ? 'rgba(127,214,255,.07)' : 'rgba(255,255,255,.04)';
      roundRect(vpX + 8, cy, vpW - 16, rowH, 10); ctx.fill();
      ctx.strokeStyle = held ? 'rgba(127,214,255,.5)' : 'rgba(255,255,255,.10)'; ctx.lineWidth = 1.3; ctx.stroke();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = '24px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(w.icon, vpX + 34, cy + rowH / 2);
      ctx.textAlign = 'left';
      ctx.font = 'bold 14px system-ui'; ctx.fillStyle = '#eaf0ff';
      ctx.fillText(w.name + (evolved ? '  →  ★ ' + S.EVO.name : held ? '  ·  equipped' : ''), vpX + 60, cy + 15);
      ctx.font = '11px system-ui'; ctx.fillStyle = '#8a97c4';
      ctx.fillText(fitTip(w.desc, vpW - 92, vpX + 60, cy + 24, 14), vpX + 60, cy + 31);
      if (w.evo) {
        const a = ARCH_INFO[w.evo.arch];
        ctx.font = 'bold 11px system-ui'; ctx.fillStyle = '#ffd66e';
        ctx.fillText(fitTip('⤴ ' + a.icon + ' ' + a.name + ' ×' + BAL.evo.need + ' → ' + w.evo.name + ' — ' + w.evo.desc, vpW - 92, vpX + 60, cy + 40, 14), vpX + 60, cy + 47);
        ctx.font = '11px system-ui'; ctx.fillStyle = '#9fb0e0';
        ctx.fillText(fitTip(evoEffectStr(w.evo), vpW - 92, vpX + 60, cy + 53, 14), vpX + 60, cy + 60);
      }
      if (w.evo2) {   // stage 2: the ascension deed + what it changes
        ctx.font = 'bold 10px system-ui'; ctx.fillStyle = '#ffe9a8';
        ctx.fillText(fitTip('⤴⤴ slay a boss while evolved → ' + w.evo2.name + ' — ' + w.evo2.desc, vpW - 92, vpX + 60, cy + 67, 14), vpX + 60, cy + 74);
      }
      const rule = MODE_RULES[w.mode] || (w.key === 'railgun' ? 'items: already pierces everything — pierce items are redundant' : null);
      if (rule) {   // how items interact with this firing mode (the "Ricochet Rig vs Arc Coil" rules)
        ctx.font = 'italic 10px system-ui'; ctx.fillStyle = '#ffb74d';
        ctx.fillText(fitTip(rule, vpW - 92, vpX + 60, cy + 81, 14), vpX + 60, cy + 88);
      }
    });
    cy0 += WEAPONS.length * (rowH + rowGap) + 8;
    ctx.textBaseline = 'alphabetic';
  } else if (S.journalTab === 'synergy') {   // synergy codex: the cross-system interactions, no longer tribal knowledge
    for (const sy of SYNERGY_INFO) {
      ctx.font = '12px system-ui';
      const maxW2 = vpW - 44;   // measure the wrapped desc first — culling & the scroll clamp need real heights
      let line = '', nl = 1;
      for (const w2 of sy.desc.split(' ')) {
        if (ctx.measureText(line + w2 + ' ').width > maxW2 && line) { nl++; line = w2 + ' '; } else line += w2 + ' ';
      }
      const rowH2 = 30 + nl * 15 + 12;
      const ry = vpY - S.journalScroll + cy0;
      if (ry + rowH2 > vpY && ry < vpY + vpH) {
        ctx.fillStyle = 'rgba(255,255,255,.03)'; roundRect(vpX + 4, ry, vpW - 8, rowH2 - 6, 10); ctx.fill();
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.font = 'bold 13px system-ui'; ctx.fillStyle = sy.col;
        ctx.fillText(sy.icon + '  ' + sy.name, vpX + 16, ry + 16);
        ctx.textBaseline = 'alphabetic';
        ctx.font = '12px system-ui'; ctx.fillStyle = '#cdd6ff';
        wrapText(sy.desc, vpX + 16, ry + 38, maxW2, 15);
      }
      cy0 += rowH2;
    }
    cy0 += 8;
  } else if (S.journalTab === 'achievements') {
    const rowH = 58, rowGap = 8;
    ACHIEVEMENTS.forEach((a, i) => {
      const got = !!(S.save.achievements || {})[a.key];
      const cy = vpY - S.journalScroll + cy0 + i * (rowH + rowGap);
      if (cy + rowH < vpY || cy > vpY + vpH) return;   // cull rows scrolled out of view
      ctx.fillStyle = got ? 'rgba(255,210,110,.08)' : 'rgba(255,255,255,.03)';
      roundRect(vpX + 8, cy, vpW - 16, rowH, 10); ctx.fill();
      ctx.strokeStyle = got ? 'rgba(255,210,110,.55)' : 'rgba(255,255,255,.10)'; ctx.lineWidth = 1.3; ctx.stroke();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.globalAlpha = got ? 1 : 0.4; ctx.font = '26px system-ui'; ctx.fillStyle = '#fff';
      ctx.fillText(got ? a.icon : '🔒', vpX + 36, cy + rowH / 2); ctx.globalAlpha = 1;
      ctx.textAlign = 'left';
      ctx.font = 'bold 15px system-ui'; ctx.fillStyle = got ? '#ffe8b0' : '#8a93b5';
      ctx.fillText(a.name, vpX + 64, cy + 23);
      ctx.font = '12px system-ui'; ctx.fillStyle = got ? '#cdd6ff' : '#6b76a0';
      ctx.fillText(a.desc, vpX + 64, cy + 42);
      if (got) { ctx.textAlign = 'right'; ctx.font = 'bold 16px system-ui'; ctx.fillStyle = '#7be08a'; ctx.fillText('✓', vpX + vpW - 18, cy + rowH / 2 + 1); ctx.textAlign = 'left'; }
    });
    cy0 += ACHIEVEMENTS.length * (rowH + rowGap) + 8;
  } else {   // damage meter: per-source bar breakdown for this run
    const rows = Object.keys(S.dmgBySrc)
      .map(k => ({ k, v: S.dmgBySrc[k], m: srcMeta(k) }))
      .filter(r => r.v > 0)
      .sort((a, b) => b.v - a.v);
    const total = rows.reduce((s, r) => s + r.v, 0);
    const fmt = abbr;   // escalates k/M/B/T… (was capped at M, garbling deep-run totals)
    if (!total) {
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = '#6b76a0'; ctx.font = '15px system-ui';
      ctx.fillText('No damage dealt yet — play a run, then check back.', vpX + vpW / 2, vpY + 50);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    } else {
      const thy = vpY - S.journalScroll + cy0;            // total header
      if (thy + 18 > vpY && thy < vpY + vpH) {
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 15px system-ui';
        ctx.fillText('Total damage  ·  ' + fmt(total), vpX + 8, thy + 10);
      }
      cy0 += 30;
      const rowH = 46, rowGap = 8, max = rows[0].v;
      rows.forEach((r, i) => {
        const cy = vpY - S.journalScroll + cy0 + i * (rowH + rowGap);
        if (cy + rowH < vpY || cy > vpY + vpH) return;   // cull rows scrolled out of view
        const col = r.m ? r.m.col : '#9fb0e0', name = r.m ? r.m.name : r.k;
        ctx.fillStyle = 'rgba(255,255,255,.04)'; roundRect(vpX + 8, cy, vpW - 16, rowH, 9); ctx.fill();
        const barW = Math.max(3, (vpW - 16) * (r.v / max));   // bar length relative to the top source
        ctx.fillStyle = hexA(col, 0.22); roundRect(vpX + 8, cy, barW, rowH, 9); ctx.fill();
        ctx.strokeStyle = hexA(col, 0.55); ctx.lineWidth = 1.3; roundRect(vpX + 8, cy, vpW - 16, rowH, 9); ctx.stroke();
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        ctx.font = 'bold 15px system-ui'; ctx.fillStyle = hexA(col, 1);
        ctx.fillText(name, vpX + 22, cy + rowH / 2);
        ctx.textAlign = 'right';
        ctx.font = 'bold 15px system-ui'; ctx.fillStyle = '#eaf0ff';
        ctx.fillText(fmt(r.v), vpX + vpW - 72, cy + rowH / 2);
        ctx.font = '12px system-ui'; ctx.fillStyle = '#9fb0e0';
        ctx.fillText((100 * r.v / total).toFixed(0) + '%', vpX + vpW - 20, cy + rowH / 2);
        ctx.textAlign = 'left';
      });
      cy0 += rows.length * (rowH + rowGap) + 8;
      ctx.textBaseline = 'alphabetic';
    }
  }
  ctx.restore();

  S.journalScrollMax = Math.max(0, cy0 - vpH);
  if (S.journalScroll > S.journalScrollMax) S.journalScroll = S.journalScrollMax;

  if (S.journalScrollMax > 0) {                         // scrollbar thumb
    const thumbH = Math.max(28, vpH * vpH / cy0);
    const thumbY = vpY + (S.journalScroll / S.journalScrollMax) * (vpH - thumbH);
    ctx.fillStyle = 'rgba(255,255,255,.14)'; roundRect(vpX + vpW - 5, thumbY, 4, thumbH, 2); ctx.fill();
  }

  const bw = 200, bh = 46, bx = px + (pw - bw) / 2, by = py + ph - bh - 16;   // close button
  const hot = pointIn(aim, bx, by, bw, bh);
  ctx.fillStyle = hot ? '#3aa0ff' : '#2b6fd6'; roundRect(bx, by, bw, bh, 12); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold 17px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('◂ CLOSE', bx + bw / 2, by + bh / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => { S.showJournal = false; } });
}
export function cycleStartWeapon(dir) {
  let i = WEAPON_IDX[S.save.startWeapon] || 0;
  i = (i + dir + WEAPONS.length) % WEAPONS.length;
  S.save.startWeapon = WEAPONS[i].key; persist(); Audio.play('click');
}
export function drawWeaponSelector(x, y, w, h) {
  const ww = WEAPONS[WEAPON_IDX[S.save.startWeapon] || 0];
  ctx.fillStyle = 'rgba(127,182,255,.10)'; roundRect(x, y, w, h, 10); ctx.fill();
  ctx.strokeStyle = 'rgba(127,182,255,.45)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.font = '22px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(ww.icon, x + 14, y + h / 2);
  ctx.font = 'bold 14px system-ui'; ctx.fillStyle = '#eaf0ff'; ctx.fillText(fitTip('Weapon: ' + ww.name, w - 124, x + 48, y + h / 2 - 15, 16), x + 48, y + h / 2 - 7);
  const evoHint = ww.evo ? '  ·  ⤴ ' + ARCH_INFO[ww.evo.arch].icon + '×' + BAL.evo.need + ' → ' + ww.evo.name : '';
  ctx.font = '11px system-ui'; ctx.fillStyle = '#9fb0e0'; ctx.fillText(fitTip(ww.desc + evoHint, w - 124, x + 48, y + h / 2 + 1, 16), x + 48, y + h / 2 + 9);
  ctx.textAlign = 'center'; ctx.font = 'bold 20px system-ui'; ctx.fillStyle = '#cdd6ff';
  ctx.fillText('◂', x + w - 54, y + h / 2); ctx.fillText('▸', x + w - 22, y + h / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: x + w - 70, y, w: 35, h, action: () => cycleStartWeapon(-1) });   // ◂ prev
  S.uiButtons.push({ x: x + w - 35, y, w: 35, h, action: () => cycleStartWeapon(1) });    // ▸ next
  S.uiButtons.push({ x, y, w: w - 70, h, action: () => cycleStartWeapon(1) });            // body cycles forward
}
export function cycleCompanion(dir) {
  let i = COMPANION_IDX[S.save.companion] || 0;
  i = (i + dir + COMPANIONS.length) % COMPANIONS.length;
  S.save.companion = COMPANIONS[i].key; persist(); Audio.play('click');
}
export function drawCompanionSelector(x, y, w, h) {
  const c = COMPANIONS[COMPANION_IDX[S.save.companion] || 0];
  ctx.fillStyle = 'rgba(180,160,255,.10)'; roundRect(x, y, w, h, 10); ctx.fill();
  ctx.strokeStyle = hexA(c.color, 0.5); ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.font = '22px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(c.icon, x + 14, y + h / 2);
  ctx.font = 'bold 14px system-ui'; ctx.fillStyle = '#eaf0ff'; ctx.fillText(fitTip('Ally: ' + c.name, w - 124, x + 48, y + h / 2 - 15, 16), x + 48, y + h / 2 - 7);
  ctx.font = '11px system-ui'; ctx.fillStyle = '#9fb0e0'; ctx.fillText(fitTip(c.desc, w - 124, x + 48, y + h / 2 + 1, 16), x + 48, y + h / 2 + 9);
  ctx.textAlign = 'center'; ctx.font = 'bold 20px system-ui'; ctx.fillStyle = '#cdd6ff';
  ctx.fillText('◂', x + w - 54, y + h / 2); ctx.fillText('▸', x + w - 22, y + h / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: x + w - 70, y, w: 35, h, action: () => cycleCompanion(-1) });   // ◂ prev
  S.uiButtons.push({ x: x + w - 35, y, w: 35, h, action: () => cycleCompanion(1) });    // ▸ next
  S.uiButtons.push({ x, y, w: w - 70, h, action: () => cycleCompanion(1) });            // body cycles forward
}
export function menu(isDead) {
  S.uiButtons = [];
  const pw = Math.min(620, S.W - 32);
  const stacked = pw < 470;   // narrow → selectors stack (readable names), which pushes the upgrade list down
  const _upTop = (stacked ? 208 : 156) + ((isDead && totalItems() > 0) ? 22 : 0);   // upgrade-list top, offset from py
  // size the panel to its content (header + selectors + 6 upgrade rows + buttons) so tall phones don't
  // leave a big empty gap below the upgrades, while short screens still cap at the viewport height
  const ph = Math.min(S.H - 32, _upTop + UPGRADES.length * 46 + 12 + 78 + 8);
  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;

  ctx.save();
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

  // backdrop
  ctx.fillStyle = 'rgba(6,10,24,.86)';
  roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();

  // title + one-line summary (title shrinks + clamps so it never runs into the coin total at the right)
  ctx.fillStyle = '#fff'; ctx.font = 'bold ' + (pw < 470 ? 26 : 34) + 'px system-ui';
  ctx.fillText(fitText(isDead ? 'Run Over' : 'VOIDGUNNER', pw - 150), px + 28, py + 50);
  ctx.fillStyle = isDead ? '#ff9aa8' : '#9fb0e0'; ctx.font = '14px system-ui';   // death recap leads, in blood-tint (hover for the full line if it truncates)
  const _cause = isDead && S.lastHitBy
    ? '☠ ' + S.lastHitBy.toUpperCase() + (S.lastHitDmg > 0 ? ` hit you for ${Math.round(S.lastHitDmg)} at ${Math.max(1, Math.ceil(S.lastHitHp))} HP` : '') + '  ·  '
    : '';
  // on touch, show the controls on their own (they'd truncate after "Best wave N", and the hover
  // tooltip that reveals truncated text doesn't exist on a phone)
  ctx.fillText(fitTip(isDead
    ? _cause + `Wave ${S.waveNum}  ·  Lv ${S.level}  ·  ${fmtTime(S.survival)} survived  ·  ${S.killsRun} kills  ·  ${S.equipment.length} gear  ·  banked ${S.coinsRun} 🪙  ·  best wave ${S.best}`
    : S.isTouch ? 'drag left=move · drag right=aim · tap to use'
    : `Best wave ${S.best}  ·  WASD move · hold mouse shoot · Space dash`,
    pw - 56, px + 28, py + 62, 18), px + 28, py + 74);

  // money
  ctx.textAlign = 'right'; ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 24px system-ui';
  ctx.fillText('🪙 ' + S.save.money, px + pw - 28, py + 50);
  ctx.textAlign = 'left';

  // unlocks line — progress, or fanfare for milestones just earned this death
  const unlockedN = UNLOCKS.filter(u => (S.save.unlocks || {})[u.key]).length;
  if (isDead && S.pendingUnlocks.length) {
    ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 13px system-ui';
    ctx.fillText(fitTip('🔓 UNLOCKED: ' + S.pendingUnlocks.map(u => u.name).join('  ·  '), pw - 130, px + 28, py + 82, 16), px + 28, py + 94);
  } else {
    const next = UNLOCKS.find(u => !(S.save.unlocks || {})[u.key]);
    ctx.fillStyle = '#9be8c0'; ctx.font = '13px system-ui';
    ctx.fillText(fitText(`🔓 ${unlockedN}/${UNLOCKS.length} unlocked` + (next ? `  ·  next: ${next.hint}` : '  ·  all unlocked!'), pw - 130), px + 28, py + 94);
  }

  ctx.textAlign = 'right'; ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 12px system-ui';
  ctx.fillText('🏅 ' + ACHIEVEMENTS.filter(a => (S.save.achievements || {})[a.key]).length + '/' + ACHIEVEMENTS.length, px + pw - 28, py + 94);
  ctx.textAlign = 'left';
  // starting weapon + companion selectors — side by side on wide panels, but STACKED full-width on
  // narrow screens so the weapon/ally names are readable instead of truncating to "W…" / "A…"
  let ry;
  if (stacked) {
    drawWeaponSelector(px + 28, py + 104, pw - 56, 44);
    drawCompanionSelector(px + 28, py + 152, pw - 56, 44);
    ry = py + 208;
  } else {
    const _selW = (pw - 56 - 10) / 2;
    drawWeaponSelector(px + 28, py + 104, _selW, 40);
    drawCompanionSelector(px + 28 + _selW + 10, py + 104, _selW, 40);
    ry = py + 156;
  }

  // run recap: the items that defined this run — ONE row, overflow folds into "+N"
  if (isDead && totalItems() > 0) {
    let rx = px + 28, hidden = 0;
    const maxRx = px + pw - 76;   // leave room for the "+N" tail
    for (const it of ITEMS) {
      const n = S.items[it.key]; if (!n) continue;
      if (rx > maxRx) { hidden++; continue; }
      ctx.font = '20px system-ui'; ctx.fillStyle = '#fff';
      ctx.fillText(it.icon, rx, ry);
      ctx.font = 'bold 11px system-ui'; ctx.fillStyle = TIERS[it.tier].color;
      ctx.fillText('×' + n, rx + 22, ry - 4);
      rx += 44;
    }
    if (hidden > 0) {
      ctx.font = 'bold 12px system-ui'; ctx.fillStyle = '#8a97c4';
      ctx.fillText('+' + hidden, rx, ry - 4);
    }
    ry += 22;
  }

  // upgrade rows — squeezed into the space above the buttons. On short screens (landscape phone) one
  // column of 6 rows can't fit, so fall back to a 2-wide grid (3 rows) — keeps every buy button tappable.
  const by = py + ph - 56 - 22;                       // top of the button row (mirrored below)
  const rowW = pw - 56;
  const upCols = (by - 12 - ry) < UPGRADES.length * 40 ? 2 : 1;   // only go 2-wide when 1-column rows would be too short to tap
  const upRows = Math.ceil(UPGRADES.length / upCols);
  const colGap = 12, colW = (rowW - colGap * (upCols - 1)) / upCols;
  const rowGap = Math.max(30, Math.min(upCols > 1 ? 56 : 46, Math.floor((by - 12 - ry) / upRows)));
  const rowH = Math.min(rowGap - 4, 48);
  UPGRADES.forEach((u, i) => {
    drawUpgradeRow(u, px + 28 + (i % upCols) * (colW + colGap), ry + Math.floor(i / upCols) * rowGap, colW, rowH);
  });

  // play / retry button + leaderboard button (grouped, centered, scaled to fit narrow panels)
  const bh = 56;
  const bSc = Math.min(1, (pw - 24) / 620);           // 180+150+120+132 + 3×10 gaps
  const lblSc = Math.max(0.7, bSc);                   // shrink button TEXT with the boxes so labels don't overflow
  const pwid = 180 * bSc, lwid = 150 * bSc, jwid = 120 * bSc, cwid = 132 * bSc, gap = 10 * bSc;
  const gw = pwid + gap + lwid + gap + jwid + gap + cwid, gx = px + (pw - gw) / 2;
  const hot = pointIn(aim, gx, by, pwid, bh);
  ctx.fillStyle = hot ? '#3aa0ff' : '#2b6fd6';
  roundRect(gx, by, pwid, bh, 14); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold ' + Math.round(22 * lblSc) + 'px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(fitText(isDead ? 'SWING AGAIN' : 'PLAY', pwid - 14), gx + pwid / 2, by + bh / 2);
  if (S.updateReady) {   // new build pending — PLAY swaps it in seamlessly (reload + auto-start)
    ctx.fillStyle = '#7dffb0'; ctx.font = 'bold 11px system-ui'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('⬆ game updated — applies when you play', gx + gw / 2, by - 8);
    ctx.textBaseline = 'middle';
  }
  S.uiButtons.push({ x: gx, y: by, w: pwid, h: bh, action: startRun });
  const lbx = gx + pwid + gap, lhot = pointIn(aim, lbx, by, lwid, bh);
  ctx.fillStyle = lhot ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.07)';
  roundRect(lbx, by, lwid, bh, 14); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold ' + Math.round(16 * lblSc) + 'px system-ui';
  ctx.fillText(fitText('🏆 Leaderboard', lwid - 10), lbx + lwid / 2, by + bh / 2);
  S.uiButtons.push({ x: lbx, y: by, w: lwid, h: bh, action: () => { S.showLeaderboard = true; } });

  const jbx = lbx + lwid + gap, jhot = pointIn(aim, jbx, by, jwid, bh);
  ctx.fillStyle = jhot ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.07)';
  roundRect(jbx, by, jwid, bh, 14); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold ' + Math.round(16 * lblSc) + 'px system-ui';
  ctx.fillText(fitText('📖 Journal', jwid - 10), jbx + jwid / 2, by + bh / 2);
  S.uiButtons.push({ x: jbx, y: by, w: jwid, h: bh, action: () => { S.showJournal = true; S.journalScroll = 0; } });

  const cbx = jbx + jwid + gap, cglow = (S.pendingAccountLevels || 0) > 0, chot = pointIn(aim, cbx, by, cwid, bh);   // Character Panel (relics + account)
  ctx.fillStyle = cglow ? 'rgba(255,210,110,.16)' : chot ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.07)';
  roundRect(cbx, by, cwid, bh, 14); ctx.fill();
  ctx.strokeStyle = cglow ? '#ffd66e' : 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold ' + Math.round(15 * lblSc) + 'px system-ui';
  ctx.fillText(fitText('☷ Lv ' + accountLevel(S.save.accountXp || 0), cwid - 10), cbx + cwid / 2, by + bh / 2);
  S.uiButtons.push({ x: cbx, y: by, w: cwid, h: bh, action: () => { S.showCharacter = true; S.charScroll = 0; S.charPickSlot = null; } });
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';

  ctx.restore();
}

export function drawUpgradeRow(u, x, y, w, h) {
  const lvl = S.save.upgrades[u.key];
  const maxed = lvl >= u.max;
  const cost = upgradeCost(u);
  const afford = !maxed && S.save.money >= cost;
  const tall = h >= 38;

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'rgba(255,255,255,.045)';
  roundRect(x, y, w, h, 10); ctx.fill();

  // buy button (right) — width scales down on narrow rows
  const bw = Math.max(70, Math.min(104, w * 0.3)), bh = h - 12, bx = x + w - bw - 8, by = y + 6;
  // pips only when they clear the buy button; otherwise a compact "Lv x/max" readout sits in their place
  const showPips = (x + w * 0.44) + u.max * 14 < bx - 10;
  const showLevel = w >= 230;
  const nameMaxW = (showPips ? w * 0.42 : showLevel ? bx - 60 : bx - 14) - 18;

  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold ' + (w < 200 ? 13 : 15) + 'px system-ui';
  ctx.fillText(fitText(u.name, nameMaxW), x + 14, tall ? y + 19 : y + h / 2 + 5);
  if (tall) {
    ctx.fillStyle = '#8a97c4'; ctx.font = '12px system-ui';
    ctx.fillText(fitText(u.desc, w * 0.42 - 18), x + 14, y + 35);
  }

  if (showPips) {
    const pipX = x + w * 0.46;
    for (let i = 0; i < u.max; i++) {
      ctx.fillStyle = i < lvl ? '#7dffb0' : 'rgba(255,255,255,.15)';
      ctx.beginPath(); ctx.arc(pipX + i * 14, y + h / 2, 4, 0, Math.PI * 2); ctx.fill();
    }
  } else if (showLevel) {
    ctx.fillStyle = maxed ? '#7dffb0' : '#9fb0e0'; ctx.font = 'bold 12px system-ui';
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    ctx.fillText(lvl + '/' + u.max, bx - 8, y + h / 2);
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  }

  const hot = pointIn(aim, bx, by, bw, bh) && afford;
  let label, col, txt;
  if (maxed) { label = 'MAX'; col = 'rgba(255,255,255,.10)'; txt = '#9fb0e0'; }
  else if (afford) { label = '🪙 ' + cost; col = hot ? '#36c07a' : '#2a9c63'; txt = '#fff'; }
  else { label = '🪙 ' + cost; col = 'rgba(120,130,160,.20)'; txt = '#7a86ad'; }
  ctx.fillStyle = col; roundRect(bx, by, bw, bh, 9); ctx.fill();
  ctx.fillStyle = txt; ctx.font = 'bold 14px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(label, bx + bw / 2, by + bh / 2);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

  if (!maxed) S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => buyUpgrade(u) });
}
export function roundRect(x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function drawWarnings() {
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (const w of S.warnings) {
    const x = w.x - cam.x, y = w.y - cam.y;
    const col = enemyColor(w.kind);
    const p = 1 - w.t / 45;                       // 0→1 as it's about to spawn
    ctx.globalAlpha = 0.35 + 0.45 * Math.abs(Math.sin(S.frame * 0.4));
    ctx.strokeStyle = col; ctx.lineWidth = w.elite ? 3 : 2;
    const r = 30 - p * 12;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = 0.9; ctx.fillStyle = col; ctx.font = 'bold 16px system-ui';
    ctx.fillText('!', x, y + 1);
  }
  ctx.globalAlpha = 1; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
}

export function drawEnemies() {
  for (const e of S.enemies) {
    if (e.dead) continue;
    const x = e.x - cam.x, y = e.y - cam.y;
    if (x < -S.W * 0.7 || x > S.W * 2.1 || y < -S.H * 0.7 || y > S.H * 1.7) continue;
    const base = enemyColor(e.kind);
    const col = e.hitFlash > 0.2 ? '#ffffff' : base;
    // glow (sprite-stamped — see glowSprite)
    const gR = e.r * 2.4;
    ctx.drawImage(glowSprite(base + 'aa', base + '00'), x - gR, y - gR, gR * 2, gR * 2);
    // body
    ctx.fillStyle = col;
    if (e.kind === 'shooter') {
      ctx.save(); ctx.translate(x, y); ctx.rotate(Math.PI / 4);
      ctx.fillRect(-e.r * 0.8, -e.r * 0.8, e.r * 1.6, e.r * 1.6); ctx.restore();
    } else {
      ctx.beginPath(); ctx.arc(x, y, e.r, 0, Math.PI * 2); ctx.fill();
    }
    if (e.kind === 'bomber') {            // pulsing fuse ring — it's primed to blow
      ctx.strokeStyle = (S.frame % 12 < 6) ? '#fff' : '#ffd66e'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, e.r + 5 + Math.sin(S.frame * 0.3) * 2, 0, Math.PI * 2); ctx.stroke();
    } else if (e.kind === 'orbiter') {    // gyroscopic outer ring
      ctx.strokeStyle = '#bff0ff'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, e.r + 5, 0.6 + S.frame * 0.1, 2.4 + S.frame * 0.1); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, e.r + 5, 3.7 + S.frame * 0.1, 5.6 + S.frame * 0.1); ctx.stroke();
    } else if (e.kind === 'splitter') {   // orbiting satellites hint that it divides
      ctx.fillStyle = base;
      for (let s = 0; s < 3; s++) {
        const a = S.frame * 0.05 + s * 2.1;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * (e.r + 8), y + Math.sin(a) * (e.r + 8), 3, 0, Math.PI * 2); ctx.fill();
      }
    } else if (e.kind === 'brute') {       // heavy: thick plating ring
      ctx.strokeStyle = '#5a6275'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(x, y, e.r - 2, 0, Math.PI * 2); ctx.stroke();
    } else if (e.kind === 'charger') {     // horn points at its heading; flashes white while winding up
      if (e.cs === 'wind') {
        ctx.strokeStyle = (S.frame % 6 < 3) ? '#fff' : base; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(x, y, e.r + 4 + Math.sin(S.frame * 0.6) * 2, 0, Math.PI * 2); ctx.stroke();
      }
      const ha = Math.atan2(player.y - e.y, player.x - e.x);
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(ha) * (e.r + 9), y + Math.sin(ha) * (e.r + 9)); ctx.stroke();
      ctx.lineCap = 'butt';
    } else if (e.kind === 'sniper') {      // red sight line while aiming
      if (e.aiming > 0) {
        const sa = Math.atan2(player.y - e.y, player.x - e.x);
        ctx.strokeStyle = 'rgba(255,90,90,' + (0.25 + 0.55 * (1 - e.aiming / 55)).toFixed(2) + ')'; ctx.lineWidth = e.aiming < 18 ? 3 : 2;   // line thickens as the shot comes
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(sa) * 620, y + Math.sin(sa) * 620); ctx.stroke();
      }
    } else if (e.kind === 'summoner') {    // pulsing "hive" ring
      ctx.strokeStyle = base; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, e.r + 5 + Math.sin(S.frame * 0.1) * 2, 0, Math.PI * 2); ctx.stroke();
    } else if (e.kind === 'weaver') {      // side fins
      ctx.strokeStyle = base; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x, y, e.r + 4, 0.4, 1.2); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, e.r + 4, 3.5, 4.3); ctx.stroke();
    } else if (e.kind === 'mortar') {      // stubby barrel pointing up
      ctx.strokeStyle = base; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y - e.r - 8); ctx.stroke();
    }
    if (e.kind === 'boss') {   // spiky ring to read as a boss; tinted by elite affix on deep waves (swift/volley/echo)
      ctx.strokeStyle = e.affixB === 'swift' ? '#7fffd0' : e.affixB === 'volley' ? '#ffb347' : e.affixB === 'echo' ? '#ff6ec7' : '#ffd66e';
      ctx.lineWidth = e.affixB ? 4 : 3;
      ctx.beginPath(); ctx.arc(x, y, e.r + 6, 0, Math.PI * 2); ctx.stroke();
    }
    // elite aura — tougher enemy, big coin payout
    if (e.elite) {
      ctx.strokeStyle = '#ffe7a0'; ctx.lineWidth = 2;
      ctx.globalAlpha = 0.55 + 0.4 * Math.sin(S.frame * 0.2);
      ctx.beginPath(); ctx.arc(x, y, e.r + 6, 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // affix aura + champion name — color-coded per affix so you can read the threat at a glance
    if (e.affix && AFFIX_INFO[e.affix]) {
      const ac = AFFIX_INFO[e.affix].aura;
      ctx.strokeStyle = ac;
      ctx.lineWidth = 2; ctx.globalAlpha = 0.5 + 0.4 * Math.sin(S.frame * 0.18 + e.r);
      ctx.beginPath(); ctx.arc(x, y, e.r + 9, 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = ac; ctx.font = 'bold 10px system-ui'; ctx.textAlign = 'center';
      ctx.fillText(AFFIX_INFO[e.affix].name, x, y - e.r - 14);
      ctx.textAlign = 'left';
    }
    // boss attack telegraph — generic, driven by the ability's stamped render-data (no bosses.js import).
    // p: 0 at wind-up start → 1 at the strike. 'aim'/'ring' read off the boss; 'circle'/'spawnpips' may land
    // on the player (actTeleAt) for AoE that drops where YOU are (saturation / collapse).
    if (e.kind === 'boss' && e.actId && e.actT > 0 && e.actTele !== 'none') {
      const p = 1 - e.actT / e.actTmax, a = (0.2 + 0.6 * p).toFixed(2);
      const tcx = e.actTeleAt === 'player' ? (player.x - cam.x) : x, tcy = e.actTeleAt === 'player' ? (player.y - cam.y) : y;
      ctx.strokeStyle = 'rgba(255,110,110,' + a + ')'; ctx.lineWidth = 3 + 2 * p;
      if (e.actTele === 'aim') {              // a drawn sight-line toward the LIVE player — break the line to dodge
        const sa = Math.atan2(player.y - e.y, player.x - e.x);
        ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(sa) * 720, y + Math.sin(sa) * 720); ctx.stroke();
        ctx.lineCap = 'butt';
      } else if (e.actTele === 'circle') {    // a ground circle filling in where the blast lands
        const rr = e.actWindR || 80;
        ctx.beginPath(); ctx.arc(tcx, tcy, rr, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = 'rgba(255,90,90,' + (0.12 * p).toFixed(2) + ')';
        ctx.beginPath(); ctx.arc(tcx, tcy, rr * p, 0, Math.PI * 2); ctx.fill();
      } else if (e.actTele === 'spawnpips') { // dots circling the boss — a brood is incoming
        ctx.fillStyle = 'rgba(255,200,110,' + a + ')';
        for (let k = 0; k < 8; k++) { const ang = k * (Math.PI / 4) + S.frame * 0.05, rad = e.r + 12 + p * 8; ctx.beginPath(); ctx.arc(tcx + Math.cos(ang) * rad, tcy + Math.sin(ang) * rad, 2.5, 0, Math.PI * 2); ctx.fill(); }
      } else {                                // 'ring' (default): a growing red ring off the boss
        ctx.beginPath(); ctx.arc(x, y, e.r + 10 + p * 40, 0, Math.PI * 2); ctx.stroke();
      }
    }
    if (e.execMark) {   // Execution Protocol mark — the next hit detonates it
      ctx.strokeStyle = '#ffd66e'; ctx.lineWidth = 2;
      ctx.globalAlpha = 0.55 + 0.4 * Math.sin(S.frame * 0.4);
      ctx.beginPath(); ctx.arc(x, y, e.r + 12, 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // status tints — frozen, burning, poisoned
    if (e.freezeT > 0) {
      ctx.strokeStyle = '#aee9ff'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(x, y, e.r + 3, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = 'rgba(170,233,255,0.25)'; ctx.beginPath(); ctx.arc(x, y, e.r, 0, Math.PI * 2); ctx.fill();
    }
    if (e.burnT > 0) {
      ctx.fillStyle = 'rgba(255,120,40,' + (0.25 + 0.2 * Math.sin(S.frame * 0.5 + e.r)) + ')';
      ctx.beginPath(); ctx.arc(x, y, e.r * 0.95, 0, Math.PI * 2); ctx.fill();
    }
    if (e.poison > 0) {
      ctx.fillStyle = 'rgba(130,230,80,0.28)';
      ctx.beginPath(); ctx.arc(x, y, e.r * 0.7, 0, Math.PI * 2); ctx.fill();
    }
    // eye
    ctx.fillStyle = '#1a1020';
    ctx.beginPath(); ctx.arc(x, y, e.r * 0.35, 0, Math.PI * 2); ctx.fill();
    // hp bar — bosses now use the big top-of-screen bar (drawBossBar), so skip the floating one for them
    if (e.hp < e.maxHp && e.kind !== 'boss') {
      const bw = e.r * 2;
      ctx.fillStyle = 'rgba(0,0,0,.5)'; ctx.fillRect(x - e.r, y - e.r - 10, bw, 5);
      ctx.fillStyle = e.kind === 'boss' ? '#ff8a3a' : '#5fdc7a';
      ctx.fillRect(x - e.r, y - e.r - 10, bw * Math.max(0, e.hp / e.maxHp), 5);
    }
    // status duration bars (player request): WHICH DoT/CC is ticking and HOW LONG it has left —
    // burn 🔥 orange, poison 🧪 green (+stack count), chill ❄ ice. Drawn UNDER the body so they
    // never collide with the HP bar / champion affix label above. Freeze keeps its ring tint.
    if (e.burnT > 0 || e.poisonT > 0 || (e.slowT > 0 && !(e.freezeT > 0))) {
      let sy2 = y + e.r + 5;
      const sbar = (frac, col) => {
        ctx.fillStyle = 'rgba(0,0,0,.45)'; ctx.fillRect(x - e.r, sy2, e.r * 2, 3);
        ctx.fillStyle = col; ctx.fillRect(x - e.r, sy2, e.r * 2 * Math.max(0, Math.min(1, frac)), 3);
        sy2 += 5;
      };
      if (e.burnT > 0) sbar(e.burnT / (e.burnTMax || e.burnT), '#ff8a3d');
      if (e.poisonT > 0) {
        sbar(e.poisonT / (e.poisonTMax || e.poisonT), '#8be04e');
        ctx.fillStyle = '#8be04e'; ctx.font = 'bold 9px system-ui'; ctx.textAlign = 'left';
        ctx.fillText('×' + e.poison, x + e.r + 3, sy2 - 1);   // poison stack count beside its bar
      }
      if (e.slowT > 0 && !(e.freezeT > 0)) sbar(e.slowT / (e.slowTMax || e.slowT), '#8fd8ff');
    }
  }
}

export function drawBullets() {
  for (const b of S.bullets) {
    const x = b.x - cam.x, y = b.y - cam.y;
    ctx.fillStyle = '#ffb86b';
    ctx.shadowColor = '#ff8d3a'; ctx.shadowBlur = 8 * S.fxShadow;
    ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
  }
}

export function drawPBullets() {
  ctx.shadowColor = '#8ffcff'; ctx.shadowBlur = 10 * S.fxShadow;
  for (const b of S.pbullets) {
    const x = b.x - cam.x, y = b.y - cam.y;
    ctx.fillStyle = b.golden ? '#ffd66e'                  // Payday: golden bullets
                  : b.hydra ? '#8be04e'                   // Hydra: venom darts
                  : b.quakeWave ? '#ffb74d'               // Seismic Echo: shockwave line
                  : b.flipped ? '#c9b3ff' : '#bdf3ff';    // returning boomerang shots tint violet
    ctx.beginPath(); ctx.arc(x, y, b.r || 4, 0, Math.PI * 2); ctx.fill();
  }
  ctx.shadowBlur = 0;
}

export function drawLasers() {
  for (const l of S.lasers) {
    ctx.globalAlpha = Math.max(0, l.life);
    ctx.strokeStyle = l.beam ? '#bdf3ff' : '#8ffcff';
    ctx.lineWidth = l.beam ? 4 + l.life * 4 : 2 + l.life * 2;   // beams are chunkier
    ctx.shadowColor = '#3ad6ff'; ctx.shadowBlur = (l.beam ? 18 : 14) * S.fxShadow;
    ctx.beginPath();
    ctx.moveTo(l.x1 - cam.x, l.y1 - cam.y);
    ctx.lineTo(l.x2 - cam.x, l.y2 - cam.y);
    ctx.stroke();
    ctx.shadowBlur = 0;
  }
  ctx.globalAlpha = 1;
}

S.fxShadow = 1;   // 0 disables expensive glow (shadowBlur) when the scene is busy — keeps FPS up
export function render() {
  S.tooltips = [];                          // hover-tooltip zones are re-registered by this frame's UI draws
  bg();                                   // full-screen backdrop, drawn unzoomed
  // perf gate: heavy scenes (epic cascades) skip shadowBlur to keep the framerate smooth
  S.fxShadow = (S.particles.length + S.effects.length + S.pbullets.length + S.bullets.length + S.coins.length) > 220 ? 0 : 1;
  ctx.save();
  if (S.shake > 0.5 && S.save.settings.shake) ctx.translate((Math.random() - 0.5) * S.shake, (Math.random() - 0.5) * S.shake);
  // zoom the world around the player's on-screen position (player stays put,
  // the world shrinks around them as you speed up)
  const psx = player.x - cam.x, psy = player.y - cam.y;
  ctx.translate(psx, psy); ctx.scale(S.zoom, S.zoom); ctx.translate(-psx, -psy);
  drawWorldEdge();
  drawArena();
  drawHazards();
  drawGroundFires();
  drawMines();
  drawRocks();
  drawAuras();
  drawMortars();
  drawCoins();
  drawPickups();
  drawShrines();
  drawWarnings();
  drawEnemies();
  drawBullets();
  drawPBullets();
  drawEffects();
  drawLasers();
  drawParticles();
  if (!S.dead) drawCompanion();
  if (!S.dead) drawMirror();
  if (!S.dead) drawPlayer();
  ctx.restore();
  if (S.flash > 0.01) {                         // cascade / death white flash — gentle, gentler still if reduced
    const cap = S.save.settings.reduceFlash ? 0.1 : 0.38;
    ctx.fillStyle = `rgba(255,255,255,${Math.min(cap, S.flash)})`;
    ctx.fillRect(0, 0, S.W, S.H);
    S.flash *= 0.8;                             // decays a touch faster so flashes are brief
  }
  if (S.started && !S.dead && S.fervorTier >= 4 && !S.save.settings.reduceFlash) {  // OVERDRIVE: steady red tint (not a flash)
    ctx.fillStyle = 'rgba(255,70,70,0.05)'; ctx.fillRect(0, 0, S.W, S.H);
  }
  if (S.started && !S.dead && S.chronoFreezeT > 0) {   // TIME FRACTURE: the world holds its breath (icy tint, fading as time resumes)
    ctx.fillStyle = `rgba(150,220,255,${0.09 * Math.min(1, S.chronoFreezeT / 40)})`; ctx.fillRect(0, 0, S.W, S.H);
  }
  if (S.started && !S.dead && S.waveEvent === 'blackout' && !S.arena.active) {   // BLACKOUT: vision collapses to a ring around you (danger telegraphs still glow through)
    const cx = player.x - cam.x, cy = player.y - cam.y, outer = 175 + Math.max(S.W, S.H) * 0.5;
    const g = ctx.createRadialGradient(cx, cy, 170, cx, cy, outer);
    g.addColorStop(0, 'rgba(2,4,12,0)'); g.addColorStop(0.5, 'rgba(2,4,12,.55)'); g.addColorStop(1, 'rgba(2,4,12,.8)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, S.W, S.H);
  }
  hud();
  drawTooltips();    // truncated-text tooltips render above everything
  syncNameField();   // show the name input only on menus
}
